import {GitHubAvatar} from '@github-ui/github-avatar'
import {ActionList, ActionMenu, Button, CounterLabel, VisuallyHidden} from '@primer/react'
import {ChevronRightIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useId} from 'react'
import type {FilterOption} from '../../utils/filter-utils'

export function MultiSelectFilter({
  label,
  selected,
  options,
  onSelectionChange,
  testId,
  className,
  nested,
  sidebarSubmenu,
}: {
  label: string
  selected: string[]
  options: FilterOption[]
  onSelectionChange: (values: string[]) => void
  testId?: string
  className?: string
  nested?: boolean
  sidebarSubmenu?: boolean
}) {
  const submenuLabelId = useId()
  if (options.length === 0) return null

  const handleSelect = (event: React.MouseEvent | React.KeyboardEvent, option: FilterOption) => {
    // Prevent ActionList.Item's afterSelect from firing, which would close the menu
    event.preventDefault()
    const values = option.rawValues ?? [option.value]
    const isSelected = values.some(v => selected.includes(v))
    if (isSelected) {
      onSelectionChange(selected.filter(v => !values.includes(v)))
    } else {
      onSelectionChange([...selected, ...values])
    }
  }

  const selectedCount = options.filter(opt => {
    const values = opt.rawValues ?? [opt.value]
    return values.some(v => selected.includes(v))
  }).length

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        {nested ? (
          <ActionList.Item role="menuitem" aria-label={sidebarSubmenu ? undefined : label}>
            {label}
            {sidebarSubmenu ? (
              <ActionList.TrailingVisual>
                {selectedCount > 0 && <CounterLabel>{selectedCount}</CounterLabel>}
                <ChevronRightIcon aria-hidden="true" />
              </ActionList.TrailingVisual>
            ) : (
              <span>...</span>
            )}
          </ActionList.Item>
        ) : (
          <Button
            variant="invisible"
            trailingVisual={TriangleDownIcon}
            className={className}
            data-testid={testId}
            count={selectedCount || undefined}
          >
            {label}
          </Button>
        )}
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="auto" aria-labelledby={sidebarSubmenu ? submenuLabelId : undefined}>
        {sidebarSubmenu && <VisuallyHidden id={submenuLabelId}>Filter by {label.toLowerCase()}</VisuallyHidden>}
        <ActionList selectionVariant="multiple" role={sidebarSubmenu ? 'menu' : undefined}>
          {options.map(opt => (
            <ActionList.Item
              key={opt.value}
              role={sidebarSubmenu ? 'menuitemcheckbox' : undefined}
              selected={opt.rawValues ? opt.rawValues.some(v => selected.includes(v)) : selected.includes(opt.value)}
              onSelect={e => handleSelect(e, opt)}
            >
              {opt.icon && (
                <ActionList.LeadingVisual>
                  <opt.icon className={opt.iconClassName} />
                </ActionList.LeadingVisual>
              )}
              {opt.avatarUrl && (
                <ActionList.LeadingVisual>
                  <GitHubAvatar src={opt.avatarUrl} size={20} alt="" />
                </ActionList.LeadingVisual>
              )}
              {opt.label}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
